/**
 * Render email-shaped HTML that came from a person.
 *
 * The one place `dangerouslySetInnerHTML` should appear for message bodies.
 * It exists because the bodies Commander renders are NOT all authored by the
 * viewer:
 *
 *   - A sequence preview renders the SEQUENCE OWNER's signature
 *     (`EmailSignature.html_body`), which reaches the viewer's browser. The
 *     signature validator caps length and nothing else — no tag or attribute
 *     filtering anywhere in the write path — so a signature containing
 *     `<img onerror=…>` is stored verbatim and executes in a COLLEAGUE's
 *     session. That is stored cross-user XSS, not self-XSS.
 *   - Inbound mail is worse still: the body is written by whoever emailed the
 *     prospect.
 *
 * Sanitizing here rather than at each call site is deliberate. Every renderer
 * added later gets the guarantee by construction, and there is one place to
 * audit. Sanitizing on the BACKEND instead was considered and rejected: what is
 * stored is what will be SENT, and a recipient's mail client — not our
 * preview — is the thing that must ultimately be safe against it. Stripping at
 * write time would silently alter outgoing mail; stripping at render time
 * protects our own users without touching what ships.
 *
 * `USE_PROFILES: { html: true }` restricts output to HTML, dropping SVG and
 * MathML wholesale. Email bodies never legitimately need either, and both
 * carry their own XSS surface (foreignObject, xlink:href), so excluding them
 * removes a class of bypass rather than playing catch-up with it.
 */

import DOMPurify from 'dompurify';
import { useMemo } from 'react';

export interface SafeHtmlProps {
  /** Untrusted HTML — a rendered email body, signature, or AI draft. */
  html: string | null | undefined;
  className?: string;
}

export function SafeHtml({ html, className }: SafeHtmlProps) {
  // Memoized on the raw string: sanitizing parses the whole document, and these
  // render inside dialogs that re-render on unrelated state.
  const clean = useMemo(
    () => DOMPurify.sanitize(html ?? '', { USE_PROFILES: { html: true } }),
    [html]
  );

  // The one sanctioned call site: `clean` is DOMPurify output, which is the
  // entire purpose of this component.
  // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized directly above
  return <div className={className} dangerouslySetInnerHTML={{ __html: clean }} />;
}
